// AdminAnalytics — cross-tenant counts (anonymized by omission).

import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

export function AdminAnalytics(): ReactElement {
  const q = useQuery({
    queryKey: ['admin-analytics'],
    queryFn: () => orpc.admin.cross_tenant_analytics({}),
  });

  if (q.isLoading) return <div className="text-sm text-ink/60">Loading…</div>;
  if (q.error) return <div className="text-sm text-bad">{friendly_error(q.error)}</div>;
  if (!q.data) return <></>;

  const t = q.data.tenants;
  const f = q.data.feature_flags;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-medium">Cross-tenant analytics</h1>
        <p className="text-xs text-ink/60 mt-1">
          Aggregate counts only — no PII surfaces here.
        </p>
      </div>

      <section className="mb-6">
        <h2 className="text-sm font-medium text-ink/70 mb-2">Tenants</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Total" value={t.total} />
          <Tile label="Active" value={t.active} tone="good" />
          <Tile label="Pending payment" value={t.pending_payment} tone="warn" />
          <Tile label="Provisioning" value={t.provisioning} tone="warn" />
          <Tile label="Past due" value={t.past_due} tone="bad" />
          <Tile label="Suspended" value={t.suspended} tone="bad" />
          <Tile label="Provisioning failed" value={t.provisioning_failed} tone="bad" />
          <Tile label="Archived" value={t.archived} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-medium text-ink/70 mb-2">Feature flags</h2>
        <div className="grid grid-cols-3 gap-3">
          <Tile label="Total" value={f.total} />
          <Tile label="Global" value={f.global} tone="info" />
          <Tile label="Tenant-specific" value={f.tenant_specific} />
        </div>
      </section>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'good' | 'bad' | 'warn' | 'info';
}): ReactElement {
  const cls =
    tone === 'good'
      ? 'text-good'
      : tone === 'bad'
        ? 'text-bad'
        : tone === 'warn'
          ? 'text-warn'
          : tone === 'info'
            ? 'text-info'
            : 'text-ink';
  return (
    <div className="bg-surface border border-line rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-ink/50">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
